import HtmlAttrId from 'client/components/core/Sidebar/components/Settings/options/HtmlAttrId'
import { createFaqAnswer } from 'src/shared/entities/faq/create-faq'
import { useDeviceModeContext } from 'client/context/DeviceModeContext'
import { useAppDispatch } from 'client/hooks/use-dispatch'
import { usePage } from 'client/store'
import { updateEntityInnerItem } from 'client/store/page/pageActions'
import pageSelectors from 'client/store/page/pageSelectors'
import { faqListIcons } from 'common/constants/iconList'
import { MasterBlockId } from 'src/shared/types/base-entity-interface'
import FaqInterface, {
  FaqItemInterface,
} from 'common/types/entities/FaqInterface'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { CheckboxControl } from 'src/editor/shared/settings/checkbox-control'
import { ColorPickerControl } from 'src/editor/shared/settings/controls/color-picker/color-picker-control'
import { PaddingGroup } from 'src/editor/shared/settings/groups/padding'
import { SizeAndPositionGroup } from 'src/editor/shared/settings/groups/size-and-position'
import { RangeElement } from 'src/editor/shared/ui-lib/range-element'
import EntityInnerItems from '../../components/EntityInnerItems'
import TextFontUpdater from '../../components/Font/TextFontUpdater'
import IconPicker from '../../components/IconPicker'
import IconPickerWithRemoveButton from '../../components/IconPickerWithRemoveButton'
import { useUpdateEntity, useUpdateProp } from '../../hooks/useUpdateProps'
import FontSize from '../../styles/FontSize'
import ShadowStyle from '../../styles/ShadowStyle'
import FaqItemInputUi from './ui/FaqItemInputUi'

type FaqSettingsProps = {
  entity: FaqInterface
}

function FaqSettings({ entity }: FaqSettingsProps) {
  const updateProp = useUpdateProp(entity)
  const updateEntity = useUpdateEntity<FaqInterface>()
  const { isMobile } = useDeviceModeContext()
  const dispatch = useAppDispatch()
  const pageLocale = usePage(pageSelectors.getPageLocale)
  const { i18n } = useTranslation()

  function updateFaqItemTitle(innerItem: FaqItemInterface) {
    return (e: React.BaseSyntheticEvent) => {
      dispatch(updateEntityInnerItem({ ...innerItem, title: e.target.value }))
    }
  }

  function createEntityInnerItem(
    parentId: string,
    masterBlockId?: MasterBlockId,
    creationItemNumber?: number,
  ) {
    const faqAnswer = createFaqAnswer(
      parentId,
      masterBlockId,
      creationItemNumber,
    )
    faqAnswer.title = i18n.getFixedT(pageLocale)(
      'entity_settings.faq_item.default_title',
    )
    return faqAnswer
  }

  const calculatedShowIcons =
    entity.showIcons === undefined ? true : entity.showIcons
  const calculatedMobileShowIcons =
    entity.mobileShowIcons === undefined ? true : entity.mobileShowIcons

  return (
    <div>
      <EntityInnerItems<FaqItemInterface>
        entity={entity}
        createEntityInnerItem={createEntityInnerItem}
        removeWarningText="entity_settings.faq_item.remove_warning"
        renderInnerItem={(innerItem, switchToItem) => (
          <FaqItemInputUi
            value={innerItem.title}
            onChange={updateFaqItemTitle(innerItem)}
            onClick={switchToItem}
          />
        )}
        addItemButtonText="entity_settings.faq_item.add"
      />
      <RangeElement
        label="entity_settings.faq.space_between.label"
        value={entity.spaceBetweenItems}
        onChange={updateProp('spaceBetweenItems')}
        min={0}
        max={50}
      />
      <RangeElement
        label="entity_settings.faq.border_radius.label"
        value={entity.itemBorderRadius}
        onChange={updateProp('itemBorderRadius')}
        min={0}
        max={20}
      />
      <CheckboxControl
        label={'entity_settings.faq.show_icons'}
        update={updateProp('showIcons')}
        mobileUpdate={updateProp('mobileShowIcons')}
        value={calculatedShowIcons}
        mobileValue={calculatedMobileShowIcons}
      />
      <IconPicker
        labelText="entity_options.icon_picker.title_collapsible_opened"
        iconList={faqListIcons}
        value={entity.iconClassName}
        update={updateProp('iconClassName')}
        searchPlaceholder="settings.components.icon_picker.placeholder"
      />
      <IconPickerWithRemoveButton
        label="entity_options.icon_picker.title_collapsible_closed"
        iconList={faqListIcons}
        value={entity.closedIconClassName}
        update={updateProp('closedIconClassName')}
      />
      <TextFontUpdater<FaqInterface>
        entity={entity}
        update={updateEntity}
        fontFamilyKey={'titleFontFamily'}
        fontWeightKey={'titleFontWeight'}
        fontStyleKey={'titleFontStyle'}
        mobileFontFamilyKey={'mobileTitleFontFamily'}
        mobileFontWeightKey={'mobileTitleFontWeight'}
        mobileFontStyleKey={'mobileTitleFontStyle'}
      />
      <FontSize
        labelText="entity_settings.faq.title_size.label"
        fontSize={entity.titleFontSize}
        mobileFontSize={entity.mobileTitleFontSize}
        update={updateProp('titleFontSize')}
        mobileUpdate={updateProp('mobileTitleFontSize')}
      />
      <ColorPickerControl
        update={updateProp('titleColor')}
        mobileUpdate={updateProp('mobileTitleColor')}
        color={entity.titleColor}
        mobileColor={entity.mobileTitleColor}
        label="entity_settings.faq.title.label"
      />
      <ColorPickerControl
        update={updateProp('backgroundColor')}
        mobileUpdate={updateProp('mobileBackgroundColor')}
        color={entity.backgroundColor}
        mobileColor={entity.mobileBackgroundColor}
        label="entity_settings.faq_background.label"
      />
      <ColorPickerControl
        update={updateProp('iconColor')}
        mobileUpdate={updateProp('mobileIconColor')}
        color={entity.iconColor}
        mobileColor={entity.mobileIconColor}
        label="icon color"
      />
      <SizeAndPositionGroup
        padding={isMobile ? entity?.mobilePadding : entity.padding}
        updatePadding={updateProp(isMobile ? 'mobilePadding' : 'padding')}
        margin={isMobile ? entity.mobileMargin : entity.margin}
        updateMargin={updateProp(isMobile ? 'mobileMargin' : 'margin')}
      />
      <ShadowStyle
        shadow={entity.boxShadow}
        mobileShadow={entity.mobileBoxShadow}
        update={updateProp('boxShadow')}
        mobileUpdate={updateProp('mobileBoxShadow')}
      />
      <PaddingGroup
        label="entity_settings.faq.item_padding.label"
        padding={isMobile ? entity.mobileItemPadding : entity.itemPadding}
        update={updateProp(isMobile ? 'mobileItemPadding' : 'itemPadding')}
      />
      <HtmlAttrId attrId={entity.htmlAttrId} />
    </div>
  )
}

export default FaqSettings
